import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../../../globalStyles';
import { RadarChart } from './RadarChart';
import { formatDisplayValue } from '../utils';
import { MAX_INDEX_SCORE } from '../../../../../Components/Beacon/constants';

type IndexPanelProps = {
  circular?: boolean;
  color: string;
  fill: string;
  flagBreakdown?: React.ReactNode;
  labels: string[];
  /** Null when this run produced no index, which the panel says in place of a score. It also gates
   * the breakdown, since a flag's contribution means nothing without the scores it moved. */
  score: number | null;
  title: string;
  values: number[];
};

export const IndexPanel = ({
  circular,
  color,
  fill,
  flagBreakdown,
  labels,
  score,
  title,
  values,
}: IndexPanelProps): React.ReactElement => (
  <IndexPanelDiv>
    <PanelBodyDiv>
      <IndexInfoDiv>
        <IndexLabel>{title}</IndexLabel>
        <IndexScoreDiv>
          {score === null ? '– –' : formatDisplayValue(score)}
          <span> / {MAX_INDEX_SCORE}</span>
        </IndexScoreDiv>
        {score !== null && flagBreakdown}
      </IndexInfoDiv>
      {score === null ? (
        <>
          <ChartFrameDiv />
          <UnscoredOverlayDiv>
            <UnscoredPill>Not scored for this assessment</UnscoredPill>
          </UnscoredOverlayDiv>
        </>
      ) : (
        <ChartFrameDiv>
          <RadarChart
            circular={circular}
            color={color}
            fill={fill}
            labels={labels}
            values={values}
          />
        </ChartFrameDiv>
      )}
    </PanelBodyDiv>
  </IndexPanelDiv>
);

// The panel is half a grid row, so the viewport says little about whether the chart has room beside
// the score. The body wraps instead: the chart keeps at least this much width or drops under the
// score and takes the full row. Wrapping rather than a container query because the styled-components
// in use here (5.1, old stylis) emits `@container` with an unbalanced brace that eats the next rule.
const CHART_MIN_WIDTH = '380px';
// Pinned rather than left to the label's width, so both panels wrap at the same card width. Wide
// enough for the widest label, "PERSISTENCE INDEX"; the column still shrinks on cards narrower than it.
const INFO_COLUMN_WIDTH = '220px';

const IndexPanelDiv = styled.section`
  position: relative;
  min-width: 0;
  box-sizing: border-box;
  padding: 24px;
  background-color: ${colors.white};
  border: 1px solid ${colors.secondaryBorder};
  border-radius: 12px;
`;

const PanelBodyDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
`;

const IndexInfoDiv = styled.div`
  display: flex;
  flex: 0 1 ${INFO_COLUMN_WIDTH};
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const IndexLabel = styled.h2`
  margin: 0;
  color: ${colors.text};
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const IndexScoreDiv = styled.div`
  color: ${colors.text};
  font-size: 46px;
  font-weight: 700;
  line-height: 1;

  span {
    color: ${colors.grayText};
    font-size: 22px;
    font-weight: 800;
  }
`;

// Square up to a cap: a radar draws inside the smaller of its two sides, so a frame taller than it
// is wide only adds blank space under the chart.
const ChartFrameDiv = styled.div`
  flex: 1 1 ${CHART_MIN_WIDTH};
  min-width: 0;
  aspect-ratio: 1;
  max-height: 360px;
`;

const UnscoredOverlayDiv = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnscoredPill = styled.span`
  padding: 4px 14px;
  border-radius: 12px;
  background-color: ${colors.greyBg};
  color: ${colors.grayText};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
